// Failed print jobs wait here with a reprint button (plan §9.6: "the job is queued with a
// reprint button and logged"). In memory for now; M3 persists it with the rest of the
// station's local data.

import type { LabelJob, PrintFailureReason } from './types.js';

export interface QueuedJob {
  job: LabelJob;
  reason: PrintFailureReason;
  message: string;
  /** ISO instant of the most recent failure. */
  failedAt: string;
  attempts: number;
}

type Listener = (jobs: readonly QueuedJob[]) => void;

export class ReprintQueue {
  #jobs: QueuedJob[] = [];
  #listeners = new Set<Listener>();

  /** Adds a failed job, or updates it if the same job failed again. */
  add(job: LabelJob, reason: PrintFailureReason, message: string, failedAt: string): void {
    const existing = this.#jobs.find((q) => q.job.id === job.id);
    this.#jobs = existing
      ? this.#jobs.map((q) =>
          q === existing ? { ...q, reason, message, failedAt, attempts: q.attempts + 1 } : q,
        )
      : [...this.#jobs, { job, reason, message, failedAt, attempts: 1 }];
    this.#emit();
  }

  remove(jobId: string): void {
    const next = this.#jobs.filter((q) => q.job.id !== jobId);
    if (next.length === this.#jobs.length) return;
    this.#jobs = next;
    this.#emit();
  }

  /** Puts a removed entry back (the station's "Undo"), at its old place in the list. */
  restore(entry: QueuedJob, index = this.#jobs.length): void {
    if (this.#jobs.some((q) => q.job.id === entry.job.id)) return;
    const at = Math.max(0, Math.min(index, this.#jobs.length));
    this.#jobs = [...this.#jobs.slice(0, at), entry, ...this.#jobs.slice(at)];
    this.#emit();
  }

  /** Oldest first. The array is replaced on every change (safe for useSyncExternalStore). */
  list(): readonly QueuedJob[] {
    return this.#jobs;
  }

  get size(): number {
    return this.#jobs.length;
  }

  subscribe(listener: Listener): () => void {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  #emit() {
    for (const l of this.#listeners) l(this.#jobs);
  }
}
